
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { TmcDirectionItem, TmcT, TmcCreateRelocationPayload } from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

// Realisations
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer } from '@vuelidate/validators'

// Composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    setup() {
        const router = useRouter()
        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()

        const state = reactive({
            jiraNumber: null as string | null,
            fromWarehouseId: null as number | null,
            toWarehouseId: null as number | null,
            //
            items: [] as { tmc: number; tmc_name: string; amount: number }[],
            //
            is_loading: false,
        })

        const data_state = reactive({
            warehouses: new DataState<WarehouseT>({}),
            second_warehouses: new DataState<WarehouseT>({}),
            tmc: new DataState<TmcT>({}),
        })

        const item_state = reactive({
            tmc: null as number | null,
            amount: null as string | null,
        })

        const state_validation = useVuelidate(
            {
                jiraNumber: { required },
                fromWarehouseId: { required },
                toWarehouseId: { required },
                items: { required },
            },
            state
        )

        const item_validation = useVuelidate(
            {
                tmc: { required },
                amount: { required, integer },
            },
            item_state
        )

        // TMC
        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        // Warehouses
        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }
        const loadSecondWarehouses = async () => {
            data_state.second_warehouses.pagination.current_page = 1

            try {
                data_state.second_warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.second_warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.second_warehouses.is_failed = false
                data_state.second_warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.second_warehouses.failure.catchError(error)

                if (!data_state.second_warehouses.failure.error_state.is_cancel) {
                    data_state.second_warehouses.is_failed = true
                }
            } finally {
                if (!data_state.second_warehouses.failure.error_state.is_cancel) {
                    data_state.second_warehouses.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            loadTMC(1)
            await loadWarehouses()
            loadSecondWarehouses()
        })

        const resetItemForm = () => {
            item_state.tmc = null
            item_state.amount = null

            item_validation.value.$reset()
        }

        const addItem = () => {
            item_validation.value.$touch()
            if (item_validation.value.$invalid) return

            const index = findItemIndexById(item_state.tmc as number, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name
            const maybe_selected_tmc = state.items.find((item) => item.tmc === item_state.tmc)

            if (maybe_selected_tmc) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item = {
                tmc: item_state.tmc as number,
                tmc_name,
                amount: Number(item_state.amount as string),
            }

            state.items.push(item)
            resetItemForm()
        }

        const removeItem = (item_index: number) => {
            state.items.splice(item_index, 1)
        }

        const mapItems = (item: {
            tmc: number
            tmc_name: string
            amount: number
        }): TmcDirectionItem => {
            return {
                inventoryItemId: item.tmc,
                amount: item.amount,
            }
        }

        const submitForm = async () => {
            if (state.is_loading) return

            if (
                state.items.length === 0 ||
                (state.items.length > 0 && (item_state.tmc || item_state.amount))
            ) {
                addItem()
            }

            state_validation.value.$touch()

            if (state_validation.value.$invalid) {
                if (state.items.length === 0) {
                    notifications.notify({
                        title: 'Ошибка валидации',
                        description: 'Для создания заявки необходимо добавить хотя бы 1 товар',
                        type: 'danger',
                    })
                }

                return
            }

            try {
                state.is_loading = true

                const payload: TmcCreateRelocationPayload = {
                    jiraNumber: state.jiraNumber as string,
                    fromWarehouseId: state.fromWarehouseId as number,
                    toWarehouseId: state.toWarehouseId as number,
                    directionItems: state.items.map(mapItems),
                }

                await tmc.createRelocation(payload)

                notifications.notify({ title: 'Перемещение создано', type: 'success' })
                router.push('/tmc/relocations')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При создании перемещения произошла ошибка',
                    error,
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            data_state,
            item_state,
            //
            state_validation,
            item_validation,
            //
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            loadSecondWarehouses,
            //
            addItem,
            removeItem,
            submitForm,
        }
    },
})
